import moment from 'moment';
import {
  ActionType,
  AutoTextField,
  FormField,
  RadioGroupField,
  SelectField,
} from '../../../components/AutoForm/AutoFieldNew';
import {moneyFieldValueType} from '../../../components/AutoForm/AutoFormNew';

import * as builderFields from '../CreateBuilder/builderFields';
import {ComboFrequencyEnum} from '../../../schema-types/globalTypes';

export type contributePlanAndSubplanAmounts = {
  planProvider?: string | null;
  singleClientAmount: moneyFieldValueType;
  recurringClientAmount: moneyFieldValueType;
  singleEmployerAmount: moneyFieldValueType;
  recurringEmployerAmount: moneyFieldValueType;
  netSingleClientAmount?: moneyFieldValueType;
  netRecurringClientAmount?: moneyFieldValueType;
};

// Shared by every source kind: frequency, amounts, fees, dates, employer side.
export type contributeFromBase = {
  comboFrequency: ComboFrequencyEnum;
  initialFee: builderFields.initialFeesFieldType;
  initialEmployerFee?: builderFields.initialFeesFieldType;
  legacyInitialFee: builderFields.DAFeeFieldType | null;
  legacyInitialEmployerFee: builderFields.DAFeeFieldType | null;
  singleClientAmount: moneyFieldValueType;
  recurringClientAmount?: moneyFieldValueType;
  singleEmployerAmount?: moneyFieldValueType;
  recurringEmployerAmount?: moneyFieldValueType;
  netSingleClientAmount?: moneyFieldValueType;
  netRecurringClientAmount?: moneyFieldValueType;
  end?: builderFields.MomentDate;
  start?: builderFields.MomentDate;
  // Hubwise source of funds per new-money portion; unused on a plan source (DBO-1583).
  sourceOfFunds?: string | null;
  employerSourceOfFunds?: string | null;
};

// Plain new cash — the backend creates a dummy cash pot on save.
export type contributeFromNewCash = contributeFromBase & {
  sourceType: 'newCash';
  cashPotId?: string; // saved dummy pot id on reload
};

// New cash funded from one of the client's bank accounts (DBO-1439).
export type contributeFromBankAccount = contributeFromBase & {
  sourceType: 'bankAccount';
  id: string; // the selected bank account's id
};

// New cash funded from the employer's default bank account — employer money,
// so only the employer side of the contribution applies.
export type contributeFromEmployerBankAccount = contributeFromBase & {
  sourceType: 'employerBankAccount';
  id: string; // the employer's bank account id
};

// Contribution moved from an existing plan.
export type contributeFromPlan = contributeFromBase & {
  sourceType: 'plan';
  id: string;
  parentPotId?: string;
  maxAmount: number;
  inSpecie?: boolean;
  full?: boolean;
};

export type contributeFromType =
  | contributeFromNewCash
  | contributeFromBankAccount
  | contributeFromEmployerBankAccount
  | contributeFromPlan;

// The selected row's entity id — undefined for new cash, which has none.
export const getContributeFromId = (
  contributeFrom: contributeFromType | undefined
): string | undefined =>
  contributeFrom && contributeFrom.sourceType !== 'newCash'
    ? contributeFrom.id
    : undefined;

export type contributeDestinationValuesType = {
  existingPotId: string | null;
  parentPotId?: string;
};

export type contributeFieldType = {
  contributeTo: string | null;
  destinationValues: contributeDestinationValuesType;
  contributeFrom?: contributeFromType;
};

export const contributeAmountField: Omit<AutoTextField, 'id' | 'label'> = {
  type: ActionType.text,
  currency: true,
  decimals: 2,
  placeholder: '0',
  initialValue: '',
  allowZero: true,
  canBeEmpty: true,
};

export const contributeFrequencyComboField: SelectField = {
  id: 'contribute.contributeFrom.comboFrequency',
  label: 'Frequency',
  type: ActionType.select,
  needsValue: true,

  options: [
    {
      value: ComboFrequencyEnum.SINGLE,
      label: 'Single',
    },
    {
      value: ComboFrequencyEnum.MONTHLY,
      label: 'Monthly',
    },
    {
      value: ComboFrequencyEnum.QUARTERLY,
      label: 'Quarterly',
    },
    {
      value: ComboFrequencyEnum.ANNUAL,
      label: 'Annual',
    },
    {
      value: ComboFrequencyEnum.MONTHLY_AND_SINGLE,
      label: 'Monthly + Single',
    },
    {
      value: ComboFrequencyEnum.QUARTERLY_AND_SINGLE,
      label: 'Quarterly + Single',
    },
    {
      value: ComboFrequencyEnum.ANNUAL_AND_SINGLE,
      label: 'Annual + Single',
    },
  ],
  initialValue: ComboFrequencyEnum.SINGLE,
};

// DBO-1583: Curo's `ahb_hubwisesourceoffunds` labels, spelt as Curo has them
// because the backend looks the option code up by label.
export const HUBWISE_SOURCES_OF_FUNDS = [
  'Dividends or Company Profits',
  'Divorce',
  'Earnings',
  'Gift',
  'Inheritance',
  'Insurance Claims',
  'Loan',
  'Lottery or Gambling Win',
  'Maturity or Surrender Of Life Assurance',
  'Other',
  'Other Court Award',
  'Sale of Investments',
  'Sale of Property Or Business',
  'Savings',
] as const;

export const contributeSourceOfFundsField: SelectField = {
  id: 'contribute.contributeFrom.sourceOfFunds',
  label: 'Source of funds',
  type: ActionType.select,
  options: HUBWISE_SOURCES_OF_FUNDS.map((label) => ({value: label, label})),
  initialValue: '',
};

export const contributeEmployerSourceOfFundsField: SelectField = {
  ...contributeSourceOfFundsField,
  id: 'contribute.contributeFrom.employerSourceOfFunds',
  label: 'Employer source of funds',
};

export const contributeFullField: RadioGroupField = {
  id: 'full',
  label: 'Full redemption?',
  type: ActionType.radioGroup,
  initialValue: false,
  horizontal: true,
  fullWidth: true,
  horizontalOptionWidthXs: 6,
  options: [
    {label: 'Full', value: true},
    {label: 'Partial', value: false},
  ],
};

export const contributeInSpecieSourceField: RadioGroupField = {
  id: 'USE_SOURCE_ID_HERE_BY_SPREADING_THIS_AND_ADDING_SOURCE_ID',
  label: 'In specie?',
  type: ActionType.radioGroup,
  horizontal: true,
  horizontalOptionWidthXs: 6,
  initialValue: false,
  fullWidth: true,
  options: [
    {label: 'In specie', value: true},
    {label: 'Cash', value: false},
  ],
};

export const ContributePlanProviderField: FormField = {
  id: `contribute.planProvider`,
  label: 'Select plan provider',
  type: ActionType.selectAutocomplete,
  options: [],
  initialValue: '',
};
export const ContributeToField: FormField = {
  id: `contribute.contributeTo`,
  label: 'Contribute to',
  initialValue: '',
  type: ActionType.text,
};
export const ContributeAmountSingleField: AutoTextField = {
  ...contributeAmountField,
  label: 'Contribute (Single)',
  id: `contribute.contributeFrom.singleClientAmount`,
  allowZero: true,
  initialValue: '',
  currency: true,
  placeholder: '0',
};
export const ContributeAmountRecurrentField: AutoTextField = {
  ...contributeAmountField,
  label: 'Contribute (Recurrent)',
  id: `contribute.contributeFrom.recurringClientAmount`,
  initialValue: '',
};
export const ContributeEndField: FormField = {
  id: `contribute.contributeFrom.end`,
  label: 'End date',
  type: ActionType.datepicker,
  fullWidth: true,
  initialValue: undefined,
};
export const ContributePersonalSingleGrossField: AutoTextField = {
  ...contributeAmountField,
  id: 'contribute.contributeFrom.singleClientAmount',
  label: 'Personal Single Amount (Gross)',
};
export const ContributePersonalSingleNetField: AutoTextField = {
  ...contributeAmountField,
  id: 'contribute.contributeFrom.netSingleClientAmount',
  label: 'Personal Single Amount (Net)',
  disabled: true,
};
export const ContributePersonalRecurringGrossField: AutoTextField = {
  ...contributeAmountField,
  id: 'contribute.contributeFrom.recurringClientAmount',
  label: 'Personal Recurring Amount (Gross)',
};
export const ContributePersonalRecurringNetField: AutoTextField = {
  ...contributeAmountField,
  id: 'contribute.contributeFrom.netRecurringClientAmount',
  label: 'Personal Recurring Amount (Net)',
  disabled: true,
};
export const ContributeEmployerSingleField: AutoTextField = {
  ...contributeAmountField,
  id: 'contribute.contributeFrom.singleEmployerAmount',
  label: 'Employer Single Amount (Gross)',
};
export const ContributeEmployerRecurringField: AutoTextField = {
  ...contributeAmountField,
  id: 'contribute.contributeFrom.recurringEmployerAmount',
  label: 'Employer Recurring Amount (Gross)',
};
export const ContributeStartField: FormField = {
  id: `contribute.contributeFrom.start`,
  label: 'Start date',
  type: ActionType.datepicker,
  fullWidth: true,
  shouldDisableDate: (day) => {
    const dateMoment = moment.utc(day);
    const date = dateMoment?.get('date');
    if (!date) {
      return true;
    }
    if (date === 1) {
      return false;
    }
    if (date === 15) {
      return false;
    }
    return true;
  },
};
